import { useCallback, useMemo, useRef, useState } from 'react';

import {
  type ApiListingRequest,
  type TableToolbarFilterConfig,
  TableWrapper,
  type TableWrapperRef,
  useModal,
  usePermissions,
} from '@bitstopco/bitstop-theme';
import { skipToken } from '@reduxjs/toolkit/query';

import cloneDeep from 'lodash/cloneDeep';
import { useLocation, useNavigate } from 'react-router';

import CardTable from '@/components/ui/card/CardTable';
import { useBulkTaggableActions } from '@/hooks/useBulkTaggableActions';
import { useGetAllTagsByTermQuery } from '@/store/api/services/common/tags';
import {
  useGetMachinesExportMutation,
  useGetMachinesQuery,
  useGetMachinesSeveritiesQuery,
} from '@/store/api/services/machines/machines';
import { useGetIntegrationsQuery } from '@/store/api/services/resources/integrations';

import { DEFAULT_PAGINATION } from '@/constants/api';
import {
  INTEGRATION_MACHINES_COLUMNS,
  INTEGRATION_MACHINES_FILTERS_CONFIG,
  INTEGRATION_MACHINES_MULTI_SELECT_CONFIG,
  MACHINES_COLUMNS,
  MACHINES_FILTERS_CONFIG,
  MACHINES_MULTI_SELECT_CONFIG,
} from '@/constants/app/machines';

import ActionsPopover from '@/components/ui/ActionPopover';
import { appendToFilters, transformMachinesFilterPayload } from '@/helpers/api';
import type { Machine, Tag, WalletCoin, WithTags } from '@/types';
import { Stack, Typography } from '@mui/material';

type MultiSelectAction = {
  id: string;
  getProps: (atms: Machine[], multiSelectValues: boolean[]) => object;
};

const getSelectedAtms = (atms: Machine[], multiSelectValues: boolean[]) =>
  multiSelectValues
    .map((isSelected, index) => isSelected && atms[index])
    .filter(Boolean) as Machine[];

// Position of "Create Case" in MACHINES_MULTI_SELECT_CONFIG - only available on the
// non-integration table, so it's handled separately from multiSelectRemoteCommandActions.
const MACHINES_CREATE_CASE_ACTION_INDEX = 6;

const multiSelectRemoteCommandActions: Record<number, MultiSelectAction> = {
  // 2: {
  //   id: 'machines-bulk-clear-cashbox',
  //   getProps: (data, multiSelectValues) => {
  //     const selectedAtms = getSelectedAtms(data, multiSelectValues);
  //     return { selectedAtms };
  //   },
  // },
  2: {
    id: 'machines-bulk-restart',
    getProps: (data, multiSelectValues) => {
      const selectedAtms = getSelectedAtms(data, multiSelectValues);
      return { selectedAtms };
    },
  },
  3: {
    id: 'machines-bulk-set-status',
    getProps: (data, multiSelectValues) => {
      const selectedAtms = getSelectedAtms(data, multiSelectValues);
      return { selectedAtms };
    },
  },
  4: {
    id: 'machines-bulk-toggle-coins',
    getProps: (data, multiSelectValues) => {
      const selectedAtms = getSelectedAtms(data, multiSelectValues);
      return { selectedAtms, enable: true };
    },
  },
  5: {
    id: 'machines-bulk-toggle-coins',
    getProps: (data, multiSelectValues) => {
      const selectedAtms = getSelectedAtms(data, multiSelectValues);
      return { selectedAtms, enable: false };
    },
  },
};

/**
 * Machines Table
 * @return {*}
 */
const MachinesAtms = () => {
  const { show } = useModal(); // Added hide function
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [exportMutation] = useGetMachinesExportMutation();

  const [payload, setPayload] = useState<ApiListingRequest>();
  const tableRef = useRef<TableWrapperRef>(null);

  const { data: tagsData } = useGetAllTagsByTermQuery({ category: 'machine', term: '' });
  const tagsList = useMemo(() => tagsData?.data ?? [], [tagsData]);
  const { data: severities = [] } = useGetMachinesSeveritiesQuery();

  // Same permission gating /cases/create (the create route's destination) in ROUTES_PERMISSION.
  const canCreateCase = usePermissions({ permission: 'cases.create' })[1];

  const isIntegration = pathname.includes('integrations');

  const { data: integrationsData } = useGetIntegrationsQuery(isIntegration ? undefined : skipToken);
  const integrationsList = useMemo(() => integrationsData?.data ?? [], [integrationsData]);

  const computedPayload = useMemo(() => {
    if (!payload) {
      return skipToken;
    }

    const clonedPayload = cloneDeep(payload);
    transformMachinesFilterPayload(clonedPayload.filter);

    return appendToFilters(clonedPayload, [
      {
        operator: isIntegration ? 'not_equal' : 'equal',
        property: 'integration_id',
        value: null,
      },
    ]);
  }, [payload, isIntegration]);

  const filtersConfig = useMemo(() => {
    const config = cloneDeep(
      isIntegration ? INTEGRATION_MACHINES_FILTERS_CONFIG : MACHINES_FILTERS_CONFIG,
    ) as Record<string, any>[];
    // Populate tags filter options
    const tagFilterIndex = config.findIndex((f: any) => f.property === 'tag_ids');
    if (tagFilterIndex !== -1) {
      config[tagFilterIndex].options = [
        ...tagsList.map(({ id, name, color }) => ({ label: name, value: id, color })),
      ];
    }
    const integrationFilterIndex = config.findIndex((f: any) => f.property === 'integration_id');
    if (integrationFilterIndex !== -1) {
      config[integrationFilterIndex].options = integrationsList
        .filter(({ id }) => id)
        .map(({ id, name }) => ({ label: name ?? id, value: id }));
    }
    if (!isIntegration) {
      // Populate status filter options
      const statusFilterIndex = config.findIndex((f: any) => f.property === 'status');
      if (statusFilterIndex !== -1) {
        config[statusFilterIndex].options = [
          ...severities.map(({ label, key }) => ({ label, value: key })),
        ];
      }
    }

    return config as TableToolbarFilterConfig[];
  }, [tagsList, severities, integrationsList, isIntegration]);

  const {
    data: {
      data: apiData = [],
      meta: { pagination: metaPagination = DEFAULT_PAGINATION } = {},
      included,
    } = {},
    isFetching,
    refetch,
  } = useGetMachinesQuery(computedPayload);

  const data: WithTags<Machine>[] = useMemo(() => {
    const allTags = (included?.tags ?? []) as Tag[];
    const allCoins = (included?.coins ?? []) as WalletCoin[];
    return apiData.map((machine) => ({
      ...machine,
      tags: allTags.filter(({ id }) => machine.tag_ids.includes(id)),
      enabled_coins: allCoins.filter(({ id }) => machine.enabled_coin_ids?.includes(id)),
    }));
  }, [apiData]);

  const { multiSelectValues, setMultiSelectValues, handleMultiSelectActions } =
    useBulkTaggableActions(data, 'machine');

  const handleExport = (type: string) => {
    tableRef.current?.getExportFile({
      type,
      mutation: exportMutation,
      fileName: 'Nonce-Atms-Report',
      overridePayload: computedPayload as ApiListingRequest,
    });
  };

  const handleRow = useCallback(({ id }: Machine) => {
    navigate(`/machine/${id}`);
  }, []);

  const handleTableActions = (index: number) => {
    if (index <= 1) return handleMultiSelectActions(index);
    // "Create Case" - last entry in MACHINES_MULTI_SELECT_CONFIG - navigates to the case
    // creation page instead of opening a modal, so it isn't part of multiSelectRemoteCommandActions.
    if (index === MACHINES_CREATE_CASE_ACTION_INDEX) {
      const selectedAtms = getSelectedAtms(data, multiSelectValues);
      navigate('/cases/create', { state: { atmIds: selectedAtms.map(({ id }) => id) } });
      return;
    }
    const modal = multiSelectRemoteCommandActions[index];
    const props = modal.getProps(data, multiSelectValues);
    show(modal.id, { ...props, refetch });
  };

  return (
    <CardTable>
      <TableWrapper<WithTags<Machine>>
        data={data}
        ref={tableRef}
        hideDateRangePicker
        isLoading={isFetching || !payload}
        sortByDefaultValue="id"
        columns={isIntegration ? INTEGRATION_MACHINES_COLUMNS : MACHINES_COLUMNS}
        filterConfigs={filtersConfig}
        totalPages={metaPagination?.total_pages}
        totalItems={metaPagination?.total_items}
        tableProps={{
          placeholder: {
            empty: {
              title: 'No machines found',
              content:
                "We can't find any machine that matches your search, please try a different request",
            },
          },
        }}
        tableToolbarProps={{
          searchPlaceholder: "Search by machine's name, serial, and ID",
        }}
        onChange={setPayload}
        onRowClick={handleRow}
        onActionMenuItemClick={handleExport}
        multiSelectValues={multiSelectValues}
        onMultiSelectValuesChange={setMultiSelectValues}
        tableMultiSelectActionsProps={{
          name: 'Machine(s)',
          config: isIntegration
            ? INTEGRATION_MACHINES_MULTI_SELECT_CONFIG
            : canCreateCase
              ? MACHINES_MULTI_SELECT_CONFIG
              : MACHINES_MULTI_SELECT_CONFIG.filter(({ label }) => label !== 'Create Case'),
          onClick: handleTableActions,
        }}
        renderMultiSelectActions={(props) => {
          if (!multiSelectValues.length) return <></>;
          return (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">
                {multiSelectValues.filter(Boolean).length} {props.name} selected
              </Typography>
              {props.config.length > 0 && (
                <ActionsPopover
                  buttonProps={{
                    size: 'small',
                  }}
                  items={props.config.map(({ label }) => ({
                    label,
                    disabled: false, // or conditionally based on selection
                  }))}
                  onClick={(index) => {
                    props.onClick?.(index);
                  }}
                />
              )}
            </Stack>
          );
        }}
      />
    </CardTable>
  );
};

export default MachinesAtms;
